/**
 * The ELASO Instance Admin Client's layout at `/instance`: the guard, the socket
 * claim, and the admin shell both admin clients share
 * (docs/adr/39-two-admin-clients-the-elaso-instance-admin-client-and-the-organisation-admin-client.md).
 *
 * The guard is a PROBE (TECH-3348). Admission is an Instance Manager owner or
 * admin relation in the permission system
 * (docs/adr/32-the-root-organisation-and-arranged-access.md), and only the
 * server can read one, so the client asks `instanceAdmin.me` and renders from
 * the answer. The same answer carries the caller's tier, which is all the pages
 * show owner-only controls from. Every procedure behind them carries
 * `.requireRootOrganizationAdmin()` and the server re-decides each call.
 *
 * "Asking" and "refused" are different states. Collapsing them would show an
 * Instance Manager the no-access page on every load until the probe answered.
 */
import type {
	InstanceAdminSelf,
	OrganizationAuthorityTier,
} from "@elaso/shared/types/organization";
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { Activity, Building2, KeyRound, LayoutDashboard } from "lucide-react";
import { useEffect, useState } from "react";
import { ulid } from "ulid";
import { useStore } from "zustand";
import { AdminNoAccess } from "@/components/admin/admin-no-access";
import { type AdminPage, AdminShell } from "@/components/admin/admin-shell";
import {
	AdminLoading,
	AdminReadFailed,
	AdminUnavailable,
} from "@/components/admin/admin-status";
import { ConnectionWaitScreen } from "@/components/auth/connection-wait";
import { InstanceAdmissionProvider } from "@/components/instance/instance-admission";
import { useConnectionWait } from "@/hooks/use-connection-wait";
import { useSendToSignIn } from "@/hooks/use-send-to-sign-in";
import { isElectron } from "@/lib/platform";
import { claimSocket } from "@/lib/socket-owner";
import { authStore } from "@/lib/stores/auth-store";
import { coreStore } from "@/lib/stores/core-store";
import { ws } from "@/lib/websocket-wrapper";

export const Route = createFileRoute("/instance")({
	head: () => ({ meta: [{ title: "Instance admin - ELASO" }] }),
	component: RouteComponent,
});

/**
 * How a failed read is re-probed: exponential backoff from half a second, five
 * attempts, then it stops. A read that has failed that often is not a blip, and
 * the client says so instead of claiming an answer it does not have.
 */
const PROBE_RETRY_BASE_MS = 500;
const PROBE_RETRY_FACTOR = 2;
const PROBE_RETRY_ATTEMPTS = 5;
const probeRetryDelayMs = (attempt: number): number =>
	PROBE_RETRY_BASE_MS * PROBE_RETRY_FACTOR ** attempt;

const PAGES = [
	{ to: "/instance", label: "Overview", icon: LayoutDashboard },
	{ to: "/instance/organizations", label: "Organisations", icon: Building2 },
	{
		to: "/instance/arranged-access",
		label: "Arranged Access",
		icon: KeyRound,
	},
	{ to: "/instance/diagnostics", label: "Diagnostics", icon: Activity },
] as const satisfies readonly AdminPage[];

const AUTHORITY: Record<OrganizationAuthorityTier, string> = {
	owner: "Instance Manager owner",
	admin: "Instance Manager administrator",
};

function RouteComponent() {
	const user = useStore(authStore, (state) => state.user);
	const initialized = useStore(authStore, (state) => state.initialized);
	const wsConnected = useStore(authStore, (state) => state.wsConnected);
	const wait = useConnectionWait(wsConnected);
	const logout = useStore(authStore, (state) => state.logout);

	// The admission answer, carrying the account it is ABOUT. Keyed rather than
	// cleared, because clearing happens in an effect and effects run after render:
	// on the render where `user` changes, the previous account's answer would
	// still be held and the pages would mount once for the wrong person.
	// Comparing ids during render closes that frame.
	//
	// `undefined` is "not asked yet", which is not the same answer as a refusal,
	// and is also what a failed read leaves behind.
	const [admission, setAdmission] = useState<
		{ firebaseUid: string; self: InstanceAdminSelf } | undefined
	>(undefined);
	// Bumped to re-run the probe after a failed read.
	const [attempt, setAttempt] = useState(0);
	// Bumped when a page changes the caller's own tier. The answer already held
	// stays on screen until the new one arrives, so the page does not flash.
	const [recheck, setRecheck] = useState(0);
	// The account whose probe gave up once the retries are spent, so the client
	// offers a way out instead of checking for ever. Keyed by uid for the same
	// reason `admission` is.
	const [readFailed, setReadFailed] = useState<string | undefined>(undefined);

	// Signed out, this client offers sign-in like every other, rather than
	// refusing the visitor before anybody knows who they are.
	useSendToSignIn();

	// Loads with the Learner Client's data, so it claims the socket for `app`.
	useEffect(() => {
		claimSocket("app").catch((error) => {
			console.error("Failed to claim the socket for /app:", error);
		});
	}, []);

	// The answer belongs to ONE connection. Authority can be revoked while the
	// socket is down, so the answer is discarded and asked again on reconnect.
	useEffect(() => {
		if (!wsConnected) {
			setAdmission(undefined);
			setAttempt(0);
			setReadFailed(undefined);
		}
	}, [wsConnected]);

	// And about ONE account: whatever belongs to somebody else is dropped when
	// the signed-in account changes.
	useEffect(() => {
		const firebaseUid = user?.uid;
		setAdmission((current) =>
			current?.firebaseUid === firebaseUid ? current : undefined
		);
		setReadFailed((current) => (current === firebaseUid ? current : undefined));
		setAttempt(0);
	}, [user?.uid]);

	// Which other clients the switcher offers. Not part of admission, so neither
	// awaited with it nor asked again on its retries.
	useEffect(() => {
		if (wsConnected && user) coreStore.getState().fetchClientAccess();
	}, [wsConnected, user]);

	// Probed on every connection. A failed READ leaves the answer undefined and
	// schedules another attempt: the server tells "you are not an Instance
	// Manager" apart from "I could not find out", and turning the second into a
	// refusal would lock an administrator out until they reloaded.
	useEffect(() => {
		if (!(wsConnected && user)) return;
		const firebaseUid = user.uid;
		let current = true;
		let retry: ReturnType<typeof setTimeout> | undefined;
		ws.sendWithPromise<InstanceAdminSelf>({
			id: ulid(),
			procedure: "instanceAdmin.me",
			data: {},
		})
			.then((answer) => {
				if (current) setAdmission({ firebaseUid, self: answer });
			})
			.catch((error) => {
				if (!current) return;
				console.error(
					"Failed to resolve instance administration access:",
					error
				);
				if (attempt >= PROBE_RETRY_ATTEMPTS) {
					setReadFailed(firebaseUid);
					return;
				}
				retry = setTimeout(
					() => setAttempt((n) => n + 1),
					probeRetryDelayMs(attempt)
				);
			});
		return () => {
			current = false;
			if (retry) clearTimeout(retry);
		};
	}, [wsConnected, user, attempt, recheck]);

	if (isElectron) {
		return (
			<AdminUnavailable message="The ELASO Instance Admin Client is only available in the web app." />
		);
	}

	// Signed out, the hook above is sending them to sign in.
	if (!(initialized && user)) {
		return <AdminLoading label="Loading..." />;
	}

	if (!wsConnected) {
		return <ConnectionWaitScreen wait={wait} />;
	}

	if (readFailed === user.uid) {
		return (
			<AdminReadFailed
				message="Could not check your access."
				onRetry={() => {
					setReadFailed(undefined);
					setAttempt(0);
				}}
			/>
		);
	}

	if (admission?.firebaseUid !== user.uid) {
		return <AdminLoading label="Checking access..." />;
	}

	const { self } = admission;
	if (!self.admitted) {
		return (
			<AdminNoAccess
				email={user.email}
				onSignOut={logout}
				refusal={{ kind: "not-an-instance-manager" }}
			/>
		);
	}

	return (
		<AdminShell
			account={user.email ?? "Unknown account"}
			authority={AUTHORITY[self.tier]}
			client="instance"
			pages={PAGES}
			title={
				<h1 className="break-words font-semibold text-lg">
					{self.instanceManagerName}
				</h1>
			}
		>
			<InstanceAdmissionProvider
				admission={self}
				onRecheck={() => {
					setAttempt(0);
					setRecheck((n) => n + 1);
				}}
			>
				<Outlet />
			</InstanceAdmissionProvider>
		</AdminShell>
	);
}
